// framework/client/core/Notifier.js

export class Notifier {
  constructor(appClient) {
    this.appClient = appClient;
    // Container for toast notifications
    this.container = null;
    // Used to generate unique IDs for modals
    this.modalCounter = 0;
    this.progressElement = null;
    this.isProgressMinimized = false;
  }

  // Initialize creates a container for toasts and some global z-index styles.
  async initialize() {
    if (this.container) return;

    this.container = document.createElement("div");
    this.container.id = "notifier-container";
    this.container.setAttribute("aria-live", "polite");
    this.container.setAttribute("aria-atomic", "true");
    Object.assign(this.container.style, {
      position: "fixed",
      top: "1rem",
      right: "1rem",
      zIndex: "var(--ab-z-toast)"
    });
    document.body.appendChild(this.container);

    const style = document.createElement("style");
    style.id = "Notifier-Style";
    style.textContent = `
      .modal-backdrop { z-index: var(--ab-z-backdrop) !important; }
      .modal { z-index: var(--ab-z-modal) !important; }
      #notifier-container .toast {
        z-index: var(--ab-z-toast);
        border-radius: var(--ab-radius-md);
        box-shadow: var(--ab-shadow-lg);
        border: 1px solid var(--bs-border-color-translucent);
      }

      /* --- Styles for Modal Progress --- */
      .progress-overlay-backdrop {
        position: fixed; top: 0; left: 0; width: 100%; height: 100%;
        background-color: rgba(var(--bs-body-bg-rgb, 0, 0, 0), 0.6);
        display: flex; align-items: center; justify-content: center;
        backdrop-filter: blur(2px);
        z-index: var(--ab-z-critical);
      }
      .progress-overlay-content {
        text-align: center; color: var(--bs-body-color);
      }
      .progress-overlay-content.card {
        background-color: var(--bs-body-bg); padding: 2rem;
        border-radius: var(--ab-radius-lg); box-shadow: var(--ab-shadow-xl);
        min-width: 300px;
      }

      /* --- Styles for Minimizable Progress Widget --- */
      .progress-widget-container {
        position: fixed;
        bottom: 1rem;
        right: 1rem;
        width: 350px;
        z-index: var(--ab-z-progress);
        background-color: var(--bs-body-bg);
        border: 1px solid var(--bs-border-color);
        border-radius: var(--ab-radius-md);
        box-shadow: var(--ab-shadow-lg);
        display: flex;
        flex-direction: column;
        transition: all var(--ab-t-slow);
      }
      .progress-widget-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 1rem;
        border-bottom: 1px solid var(--bs-border-color-translucent);
        background-color: var(--bs-tertiary-bg);
        border-top-left-radius: var(--ab-radius-md);
        border-top-right-radius: var(--ab-radius-md);
      }
      .progress-widget-title {
        font-weight: var(--ab-weight-medium);
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .progress-widget-body {
        padding: 1rem;
        transition: padding var(--ab-t-slow), max-height var(--ab-t-slow);
        max-height: 300px;
        overflow: hidden;
      }
      .progress-widget-container.minimized .progress-widget-body {
        max-height: 0;
        padding-top: 0;
        padding-bottom: 0;
      }
      .progress-widget-container.minimized .progress-widget-header {
        border-bottom: none;
        border-bottom-left-radius: var(--ab-radius-md);
        border-bottom-right-radius: var(--ab-radius-md);
      }
    `;
    document.head.appendChild(style);
  }

  // --- NEW: alert() method that error() and warning() will call ---
  async alert(message, options = {}) {
    const { title, type, okButtonText, customClass, showIcon, size } =
      this._getOptions(options, "alert");
    return this._showModal({
      title,
      message,
      type,
      okButtonText,
      customClass,
      showCancel: false, // Alerts only have an OK button
      showIcon,
      size
    });
  }

  // Confirm modal (OK and Cancel buttons) 
  async confirm(message, options = {}) {
    const { title, type, okButtonText, cancelButtonText, customClass, showIcon, size } =
      this._getOptions(options, "confirm");
    return this._showModal({
      title,
      message,
      type,
      okButtonText,
      cancelButtonText,
      customClass,
      showCancel: true,
      showIcon,
      size
    });
  }

  // Prompt modal with an input field. Resolves with the input value or null if cancelled.
  async prompt(message, options = {}) {
    const { title, type, okButtonText, cancelButtonText, customClass, showIcon, size, inputType, defaultValue } =
      this._getOptions(options, "prompt");
    return new Promise((resolve) => {
      const modalId = `modal-${++this.modalCounter}`;
      const modal = this._createPromptModal({
        id: modalId,
        title,
        message,
        type,
        okButtonText,
        cancelButtonText,
        customClass,
        showIcon,
        size,
        inputType,
        defaultValue,
        onConfirm: () => {
          const input = document.getElementById(`${modalId}-input`);
          resolve(input ? input.value : defaultValue);
          bootstrap.Modal.getInstance(modal)?.hide();
        },
        onCancel: () => {
          resolve(null);
          bootstrap.Modal.getInstance(modal)?.hide();
        }
      });
      document.body.appendChild(modal);
      modal.addEventListener("hidden.bs.modal", () => {
        modal.remove();
      }, { once: true });
      const bsModal = new bootstrap.Modal(modal);
      bsModal.show();
    });
  }

  // --- FIX: error() now correctly calls this.alert() ---
  async error(message, options = {}) {
    options.type = options.type || "error";
    return this.alert(message, options);
  }

  // --- FIX: warning() now correctly calls this.alert() ---
  async warning(message, options = {}) {
    options.type = options.type || "warning";
    return this.alert(message, options);
  }

  async notify(message, options = {}) {
    return this.toast(message, options);
  }

  async success(message, options = {}) {
    options.type = options.type || "success";
    return this.notify(message, options);
  }

  async warn(message, options = {}) {
    options.type = options.type || "warning";
    return this.toast(message, options);
  }

  async info(message, options = {}) {
    options.type = options.type || "info";
    return this.toast(message, options);
  }

  // Toast notification (appended to the toast container)
  async toast(message, options = {}) {
    const { title, type, duration, customClass, showIcon } = this._getOptions(options, "toast");
    const toastEl = this._createToast(title, message, type, customClass, showIcon);
    this.container.appendChild(toastEl);
    const bsToast = new bootstrap.Toast(toastEl, {
      autohide: true,
      delay: duration
    });
    toastEl.addEventListener("hidden.bs.toast", () => {
      toastEl.remove();
    }, { once: true });
    bsToast.show();
  }

  // --- NEW: Desktop notifications using browser-native Notification API and appClient permissions ---
  async desktopNotification(title, body, options = {}) {
    if (!("Notification" in window)) {
      return;
    }

    // Only trigger notification if the app is backgrounded and not active
    const isBackground = (this.appClient.platform && typeof this.appClient.platform.isForeground !== 'undefined')
      ? !this.appClient.platform.isForeground
      : (document.hidden || !document.hasFocus());

    if (!isBackground) {
      return;
    }

    const show = () => {
      const notif = new Notification(title, {
        body: body,
        icon: options.icon || "/favicon.ico",
        tag: options.tag || undefined,
        requireInteraction: options.requireInteraction || false
      });
      notif.onclick = () => {
        window.focus();
        if (options.action) {
          window.location.hash = options.action;
        }
        notif.close();
      };
    };

    const isGiven = await this.appClient.permissions.isPermissionGiven('notifications');
    if (isGiven) {
      show();
      return;
    }

    const isDenied = await this.appClient.permissions.isPermissionDenied('notifications');
    if (isDenied) {
      return;
    }

    const result = await this.appClient.permissions.getPermission('notifications');
    if (result.status === 'granted') {
      show();
    }
  }



  // Merge provided options with defaults.
  // For alerts, confirms, and prompts the default size is set to "sm" (small).
  _getOptions(options, context) {
    const defaultType = context === "confirm" ? "primary" : "info";
    // For alert, confirm and prompt, we want a small dialog by default
    const defaultSize = (context === "alert" || context === "confirm" || context === "prompt") ? "sm" : "";
    const defaults = {
      title: options.title || (context.charAt(0).toUpperCase() + context.slice(1)),
      type: options.type || defaultType,
      duration: options.duration || 3000,
      position: options.position || "top-right",
      closable: typeof options.closable !== "undefined" ? options.closable : true,
      showIcon: typeof options.showIcon !== "undefined" ? options.showIcon : true,
      okButtonText: options.okButtonText || "OK",
      cancelButtonText: options.cancelButtonText || "Cancel",
      customClass: options.customClass || "",
      size: options.size || defaultSize
    };
    // Additional prompt-specific options
    if (context === "prompt") {
      defaults.inputType = options.inputType || "text";
      defaults.defaultValue = options.defaultValue || "";
    }
    return { ...defaults, ...options };
  }

  // Map special types (such as "error") to Bootstrap button colors.
  _mapButtonType(type) {
    const lower = type.toLowerCase();
    if (lower === "error") return "danger";
    if (lower === "alert") return "info";
    return lower;
  }

  // Return the appropriate Bootstrap Icon class based on the type.
  _getIconClass(type) {
    const lower = type.toLowerCase();
    switch (lower) {
      case "error":
      case "danger":
        return "bi-exclamation-octagon-fill";
      case "warning":
        return "bi-exclamation-triangle-fill";
      case "success":
        return "bi-check-circle-fill";
      case "info":
      case "primary":
        return "bi-info-circle-fill";
      default:
        return "bi-info-circle-fill";
    }
  }

  // Return a color class for the icon.
  _getIconColor(type) {
    const lower = type.toLowerCase();
    switch (lower) {
      case "error":
      case "danger":
        return "text-danger";
      case "warning":
        return "text-warning";
      case "success":
        return "text-success";
      case "info":
        return "text-info";
      case "primary":
        return "text-primary";
      default:
        return "";
    }
  }

  // Creates and displays a modal (used by alert and confirm)
  _showModal({ title, message, type, okButtonText, cancelButtonText, customClass, showCancel, showIcon, size, onConfirm, onCancel }) {
    return new Promise((resolve) => {
      const modalId = `modal-${++this.modalCounter}`;
      const modal = this._createModal({
        id: modalId,
        title,
        message,
        type,
        okButtonText,
        cancelButtonText,
        customClass,
        showCancel,
        showIcon,
        size,
        onConfirm: () => {
          resolve(true);
          bootstrap.Modal.getInstance(modal)?.hide();
        },
        onCancel: () => {
          resolve(false);
          bootstrap.Modal.getInstance(modal)?.hide();
        }
      });
      // Append modals to document.body for proper Bootstrap sizing
      document.body.appendChild(modal);
      modal.addEventListener("hidden.bs.modal", () => {
        modal.remove();
      }, { once: true });
      const bsModal = new bootstrap.Modal(modal);
      bsModal.show();
    });
  }

  // Builds the modal element’s HTML for alert/confirm.
  _createModal({ id, title, message, type, okButtonText, cancelButtonText, customClass, showCancel, showIcon, size, onConfirm, onCancel }) {
    const bootstrapType = this._mapButtonType(type);
    let iconHTML = "";
    if (showIcon) {
      iconHTML = `<i class="bi ${this._getIconClass(type)} me-2 ${this._getIconColor(type)}"></i>`;
    }
    let headerHTML = "";
    if (title) {
      headerHTML = `
        <div class="modal-header">
          <h5 class="modal-title">${iconHTML}${this.t(title)}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>`;
    }
    const cancelButtonHTML = showCancel ? `
      <button type="button" class="btn btn-secondary" id="${id}-cancel">
        ${this.t(cancelButtonText)}
      </button>` : "";
    // Set a size class and max-width on the dialog (for example, "sm" is 300px wide)
    const sizeClass = size ? `modal-${size}` : "";
    let modalDialogStyle = "";
    if (size === "sm") {
      modalDialogStyle = 'max-width:300px;';
    } else if (size === "lg") {
      modalDialogStyle = 'max-width:800px;';
    } else if (size === "xl") {
      modalDialogStyle = 'max-width:1140px;';
    }
    // The modal-content gets a max-height (with scrollable content) so it doesn’t fill the viewport vertically.
    const modalHTML = `
      <div class="modal-dialog modal-dialog-centered ${sizeClass}" role="document" style="${modalDialogStyle}">
        <div class="modal-content" style="max-height: calc(100vh - 3rem); overflow-y: auto;">
          ${headerHTML}
          <div class="modal-body">${this.t(message)}</div>
          <div class="modal-footer">
            ${cancelButtonHTML}
            <button type="button" class="btn btn-${bootstrapType}" id="${id}-ok">
              ${this.t(okButtonText)}
            </button>
          </div>
        </div>
      </div>`;
    const modal = document.createElement("div");
    modal.id = id;
    modal.className = `modal fade ${customClass}`.trim();
    modal.setAttribute("tabindex", "-1");
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-hidden", "true");
    modal.innerHTML = modalHTML;
    // Use requestAnimationFrame to ensure buttons exist before attaching events.
    requestAnimationFrame(() => {
      const okButton = document.getElementById(`${id}-ok`);
      const cancelButton = showCancel ? document.getElementById(`${id}-cancel`) : null;
      if (okButton) {
        okButton.addEventListener("click", onConfirm, { once: true });
      }
      if (cancelButton) {
        cancelButton.addEventListener("click", onCancel, { once: true });
      }
    });
    return modal;
  }

  // Builds a prompt modal that also includes an input field.
  _createPromptModal({ id, title, message, type, okButtonText, cancelButtonText, customClass, showIcon, size, inputType, defaultValue, onConfirm, onCancel }) {
    const bootstrapType = this._mapButtonType(type);
    let iconHTML = "";
    if (showIcon) {
      iconHTML = `<i class="bi ${this._getIconClass(type)} me-2 ${this._getIconColor(type)}"></i>`;
    }
    let headerHTML = "";
    if (title) {
      headerHTML = `
        <div class="modal-header">
          <h5 class="modal-title">${iconHTML}${this.t(title)}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>`;
    }
    const cancelButtonHTML = `
      <button type="button" class="btn btn-secondary" id="${id}-cancel">
        ${this.t(cancelButtonText)}
      </button>`;
    const sizeClass = size ? `modal-${size}` : "";
    let modalDialogStyle = "";
    if (size === "sm") {
      modalDialogStyle = 'max-width:300px;';
    } else if (size === "lg") {
      modalDialogStyle = 'max-width:800px;';
    } else if (size === "xl") {
      modalDialogStyle = 'max-width:1140px;';
    }
    // The modal body contains the message along with an input field.
    const modalHTML = `
      <div class="modal-dialog modal-dialog-centered ${sizeClass}" role="document" style="${modalDialogStyle}">
        <div class="modal-content" style="max-height: calc(100vh - 3rem); overflow-y: auto;">
          ${headerHTML}
          <div class="modal-body">
            <div>${this.t(message)}</div>
            <input type="${inputType}" id="${id}-input" class="form-control mt-2" value="${defaultValue}"/>
          </div>
          <div class="modal-footer">
            ${cancelButtonHTML}
            <button type="button" class="btn btn-${bootstrapType}" id="${id}-ok">
              ${this.t(okButtonText)}
            </button>
          </div>
        </div>
      </div>`;
    const modal = document.createElement("div");
    modal.id = id;
    modal.className = `modal fade ${customClass}`.trim();
    modal.setAttribute("tabindex", "-1");
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-hidden", "true");
    modal.innerHTML = modalHTML;
    requestAnimationFrame(() => {
      const okButton = document.getElementById(`${id}-ok`);
      const cancelButton = document.getElementById(`${id}-cancel`);
      if (okButton) {
        okButton.addEventListener("click", onConfirm, { once: true });
      }
      if (cancelButton) {
        cancelButton.addEventListener("click", onCancel, { once: true });
      }
    });
    return modal;
  }

  // Builds a toast element.
  _createToast(title, message, type, customClass, showIcon) {
    const bootstrapType = this._mapButtonType(type);
    const iconHTML = showIcon ? `<i class="bi ${this._getIconClass(type)} me-2 ${this._getIconColor(type)}"></i>` : "";
    const toastHTML = `
      <div class="toast align-items-center text-bg-${bootstrapType} ${customClass}" role="alert" aria-live="assertive" aria-atomic="true">
        <div class="toast-header">
          ${iconHTML}
          ${title ? `<strong class="me-auto">${this.t(title)}</strong>` : ""}
          <button type="button" class="btn-close ms-auto" data-bs-dismiss="toast" aria-label="Close"></button>
        </div>
        <div class="toast-body">${this.t(message)}</div>
      </div>`;
    const toastWrapper = document.createElement("div");
    toastWrapper.innerHTML = toastHTML;
    return toastWrapper.firstElementChild;
  }

  t(text, replacements) {
    return this.appClient.tx.t(text, replacements);
  }

  showProgress(options = {}) {
    this.hideProgress();

    const {
      title = 'Processing...',
      message = 'Please wait.',
      modal = false,
      isDialog = false
    } = options;

    this.isProgressMinimized = false;

    if (modal || isDialog) {
      this.progressElement = document.createElement('div');
      this.progressElement.className = 'progress-overlay-backdrop';
      this.progressElement.innerHTML = `
        <div class="progress-overlay-content card">
          <div class="progress-overlay-body">
            <div class="progress-spinner mb-3"><div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div></div>
            <div class="progress" style="display: none;"><div class="progress-bar" role="progressbar" style="width: 0%;" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100">0%</div></div>
            <h5 class="progress-title mt-2">${this.t(title)}</h5>
            <p class="progress-message text-muted mb-0">${this.t(message)}</p>
          </div>
        </div>`;
    } else {
      this.progressElement = document.createElement('div');
      this.progressElement.className = 'progress-widget-container';
      this.progressElement.innerHTML = `
        <div class="progress-widget-header">
          <h6 class="progress-widget-title">${this.t(title)}</h6>
          <div class="progress-widget-controls d-flex gap-1">
            <button class="ab-icon-btn ab-icon-btn-sm text-secondary" id="progress-minimize-btn" title="${this.t('Minimize')}" aria-label="${this.t('Minimize')}"><i class="bi bi-dash-lg"></i></button>
            <button class="ab-icon-btn ab-icon-btn-sm text-danger" id="progress-close-btn" title="${this.t('Close')}" aria-label="${this.t('Close')}"><i class="bi bi-x-lg"></i></button>
          </div>
        </div>
        <div class="progress-widget-body">
          <p class="progress-message text-muted mb-2">${this.t(message)}</p>
          <div class="progress-spinner mb-2" style="text-align: center;"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>
          <div class="progress" style="display: none;"><div class="progress-bar" role="progressbar" style="width: 0%;" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100">0%</div></div>
        </div>`;

      this.progressElement.querySelector('#progress-minimize-btn').addEventListener('click', () => {
        this.isProgressMinimized = !this.isProgressMinimized;
        this.progressElement.classList.toggle('minimized', this.isProgressMinimized);
        const icon = this.progressElement.querySelector('#progress-minimize-btn i');
        icon.className = this.isProgressMinimized ? 'bi bi-arrows-angle-expand' : 'bi bi-dash-lg';
        this.setProgress({});
      });

      this.progressElement.querySelector('#progress-close-btn').addEventListener('click', () => this.hideProgress());
    }

    document.body.appendChild(this.progressElement);
  }

  setProgress(options = {}) {
    if (!this.progressElement) return;

    const { progress, message, title } = options;

    if (typeof progress === 'number') {
      const spinner = this.progressElement.querySelector('.progress-spinner');
      const progressBarContainer = this.progressElement.querySelector('.progress');
      const progressBar = this.progressElement.querySelector('.progress-bar');

      if (spinner) spinner.style.display = 'none';
      if (progressBarContainer) progressBarContainer.style.display = 'flex';

      if (progressBar) {
        const p = Math.max(0, Math.min(100, progress));
        progressBar.style.width = `${p}%`;
        progressBar.setAttribute('aria-valuenow', p);
        progressBar.textContent = `${p}%`;
      }
    }

    if (message) {
      const messageEl = this.progressElement.querySelector('.progress-message');
      if (messageEl) messageEl.textContent = this.t(message);
    }
    
    const titleEl = this.progressElement.querySelector('.progress-title, .progress-widget-title');
    if (titleEl) {
        const baseTitle = title || titleEl.dataset.baseTitle || titleEl.textContent;
        if (!titleEl.dataset.baseTitle) titleEl.dataset.baseTitle = baseTitle;

        if (this.isProgressMinimized && typeof progress === 'number') {
            titleEl.textContent = `${this.t(baseTitle)} (${progress}%)`;
        } else {
            titleEl.textContent = this.t(baseTitle);
        }
    }
  }

  hideProgress() {
    if (this.progressElement) {
      this.progressElement.remove();
      this.progressElement = null;
    }
  }
}